import { useCallback, useEffect, useRef, useState } from "react";
import { Archive, Copy, ImagePlus, Pencil, Plus, RefreshCw, X } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import Combobox from "@/components/Combobox";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function publicUrlFor(slug) {
  if (typeof window === "undefined") return `/r/${slug}`;
  return `${window.location.origin}/r/${slug}`;
}

function StatusPill({ status }) {
  const isActive = status === "active";
  return (
    <span
      className={`inline-block text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${
        isActive ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"
      }`}
    >
      {status || "—"}
    </span>
  );
}

function AddPartnerModal({ open, onClose, onCreated }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pickedUser, setPickedUser] = useState(null);
  const [typedEmail, setTypedEmail] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSlug(""); setName(""); setPickedUser(null); setTypedEmail("");
      setLogo(null); setBusy(false); setErr("");
    }
  }, [open]);

  // Lazy-load the admin user list the first time the modal opens.
  useEffect(() => {
    if (!open || users.length > 0) return;
    setUsersLoading(true);
    api.get("/admin/users").then(({ data }) => {
      setUsers((data || []).filter((u) => u.user_id));
    }).catch(() => {
      /* admin should retry — silent here keeps the form usable */
    }).finally(() => setUsersLoading(false));
  }, [open, users.length]);

  if (!open) return null;

  // Resolved email comes from whichever the admin used:
  //   1. picked an existing user → use that user's email.
  //   2. typed an email that doesn't match a user → use the typed text
  //      (the backend pre-provisions a new partner_admin user for it).
  const resolvedEmail = pickedUser?.email?.trim().toLowerCase()
    || typedEmail.trim().toLowerCase();
  const emailValid = !!resolvedEmail && EMAIL_RE.test(resolvedEmail);

  async function submit(e) {
    e?.preventDefault();
    setErr("");
    if (!slug.trim() || !name.trim()) {
      setErr("slug and display name are required");
      return;
    }
    if (!emailValid) {
      setErr("pick an existing user or type a valid email to pre-provision them");
      return;
    }
    if (!logo) {
      setErr("logo file is required");
      return;
    }
    const form = new FormData();
    form.append("slug", slug.trim().toLowerCase());
    form.append("name", name.trim());
    form.append("partner_admin_email", resolvedEmail);
    form.append("logo", logo);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partners", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreated?.(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "create_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-partner-backdrop"
    >
      <div
        className="bg-white rounded-[14px] w-full max-w-md p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-partner-modal"
      >
        <div className="flex items-center justify-between">
          <div className="font-display text-xl font-extrabold">Add partner</div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black">
            <X size={18} />
          </button>
        </div>
        <p className="text-[12.5px] text-neutral-500 mt-1.5 leading-relaxed">
          Creates the partner record, pre-provisions the partner-admin user
          (auto-approved), and uploads their logo. They can sign in with Google
          using the email below.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3.5">
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Slug</label>
            <input
              className="input mt-1"
              type="text"
              placeholder="soar"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              autoComplete="off"
              data-testid="add-partner-slug"
            />
            <div className="text-[11px] text-neutral-500 mt-1">
              Public URL will be <span className="font-mono">{publicUrlFor(slug || "your-slug")}</span>
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Display name</label>
            <input
              className="input mt-1"
              type="text"
              placeholder="SOAR Travel"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="add-partner-name"
            />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">
              Partner admin {usersLoading && <span className="opacity-60 normal-case">loading…</span>}
            </label>
            <div className="mt-1">
              <Combobox
                value={pickedUser}
                onChange={(u) => { setPickedUser(u); if (u) setTypedEmail(""); }}
                onQueryChange={setTypedEmail}
                items={users}
                getKey={(u) => u.user_id}
                getSearch={(u) => `${u.email || ""} ${u.name || ""} ${u.user_id}`}
                renderItem={(u, opts) => (
                  opts?.compact
                    ? (u.email || u.user_id)
                    : <span><span className="font-medium">{u.email || "(no email)"}</span>{u.name && <span className="text-neutral-500"> · {u.name}</span>}</span>
                )}
                placeholder="Search existing users by email, name, or id…"
                emptyText={usersLoading
                  ? "Loading users…"
                  : (typedEmail && EMAIL_RE.test(typedEmail))
                    ? `No match — will pre-provision ${typedEmail.trim().toLowerCase()}`
                    : "No matches — type a full email to pre-provision a new partner admin"}
                testId="add-partner-admin"
              />
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              {pickedUser
                ? `Will use existing user ${pickedUser.email || pickedUser.user_id}.`
                : (typedEmail && EMAIL_RE.test(typedEmail))
                  ? `No existing user — backend will pre-provision ${typedEmail.trim().toLowerCase()}.`
                  : "Pick an existing user, or type an email to pre-provision a brand-new partner admin."}
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Logo</label>
            <div className="mt-1 flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5"
              >
                <ImagePlus size={13} /> {logo ? "Change file" : "Pick file"}
              </button>
              {logo && (
                <div className="text-[12px] text-neutral-700 truncate min-w-0">{logo.name}</div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/gif,image/webp"
                className="hidden"
                onChange={(e) => setLogo(e.target.files?.[0] || null)}
                data-testid="add-partner-logo"
              />
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">PNG, JPG, SVG, GIF, or WebP — max 1 MB.</div>
          </div>
          {err && (
            <div className="text-[12.5px] text-red-600">{err}</div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost text-[12.5px]" disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary text-[12.5px]"
              disabled={busy || !emailValid || !slug.trim() || !name.trim() || !logo}
              data-testid="add-partner-submit"
            >
              {busy ? <span className="spinner" /> : "Create partner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPartnerModal({ partner, onClose, onUpdated }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (partner) {
      setSlug(partner.slug || "");
      setName(partner.name || "");
      setLogo(null);
      setBusy(false);
      setErr("");
    }
  }, [partner]);

  if (!partner) return null;

  const slugChanged = slug.trim().toLowerCase() !== (partner.slug || "").toLowerCase();
  const nameChanged = name.trim() !== (partner.name || "");
  const slugValid = slug.trim() === "" || SLUG_RE.test(slug.trim().toLowerCase());
  const nothingChanged = !slugChanged && !nameChanged && !logo;

  async function submit(e) {
    e?.preventDefault();
    setErr("");
    if (!slug.trim() || !name.trim()) {
      setErr("slug and display name can't be empty");
      return;
    }
    if (!slugValid) {
      setErr("slug must be 2–32 chars, lowercase letters/digits/hyphens, no leading/trailing hyphen");
      return;
    }
    if (nothingChanged) {
      setErr("nothing to update");
      return;
    }
    const form = new FormData();
    if (slugChanged) form.append("slug", slug.trim().toLowerCase());
    if (nameChanged) form.append("name", name.trim());
    if (logo) form.append("logo", logo);
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/partners/${partner.id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated?.(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-partner-backdrop"
    >
      <div
        className="bg-white rounded-[14px] w-full max-w-md p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-partner-modal"
      >
        <div className="flex items-center justify-between">
          <div className="font-display text-xl font-extrabold">Edit partner</div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black">
            <X size={18} />
          </button>
        </div>
        <p className="text-[12.5px] text-neutral-500 mt-1.5 leading-relaxed">
          Editing <span className="font-mono">{partner.id}</span>. The linked partner admin
          can't be changed here — direct DB edit if you need to re-assign.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3.5">
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Slug</label>
            <input
              className="input mt-1"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              autoComplete="off"
              data-testid="edit-partner-slug"
            />
            <div className="text-[11px] text-neutral-500 mt-1">
              Public URL will be <span className="font-mono">{publicUrlFor(slug || "your-slug")}</span>
            </div>
            {slugChanged && (
              <div className="text-[11px] text-amber-700 mt-1">
                Changing the slug breaks any existing <span className="font-mono">/r/{partner.slug}</span> links you've shared.
              </div>
            )}
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Display name</label>
            <input
              className="input mt-1"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-partner-name"
            />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Logo</label>
            <div className="mt-1 flex items-center gap-3">
              {!logo && partner.logo_url && (
                <img
                  src={`${BACKEND_URL}${partner.logo_url}`}
                  alt=""
                  className="h-10 w-10 rounded-md border border-black/5 bg-white object-contain shrink-0"
                />
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5"
              >
                <ImagePlus size={13} /> {logo ? "Change file" : "Replace logo"}
              </button>
              {logo && (
                <div className="text-[12px] text-neutral-700 truncate min-w-0">{logo.name}</div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/gif,image/webp"
                className="hidden"
                onChange={(e) => setLogo(e.target.files?.[0] || null)}
                data-testid="edit-partner-logo"
              />
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              {logo ? "PNG, JPG, SVG, GIF, or WebP — max 1 MB." : "Leave blank to keep the current logo."}
            </div>
          </div>
          {err && <div className="text-[12.5px] text-red-600">{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost text-[12.5px]" disabled={busy}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary text-[12.5px]"
              disabled={busy || nothingChanged || !slug.trim() || !name.trim() || !slugValid}
              data-testid="edit-partner-submit"
            >
              {busy ? <span className="spinner" /> : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/partners");
      setPartners(data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function copyLink(slug) {
    try {
      await navigator.clipboard.writeText(publicUrlFor(slug));
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 1500);
    } catch {
      /* ignore */
    }
  }

  async function archive(partner) {
    if (!window.confirm(`Archive ${partner.name}? Existing attribution stays, but new visitors to /r/${partner.slug} will see a plain Landing page.`)) return;
    try {
      await api.delete(`/admin/partners/${partner.id}`);
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "archive_failed");
    }
  }

  return (
    <AdminLayout
      activeTab="partners"
      title="Partners"
      right={
        <div className="flex items-center gap-2">
          <button onClick={refresh} disabled={loading} className="btn btn-ghost text-[12.5px]">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5"
            data-testid="add-partner-open"
          >
            <Plus size={13} /> Add partner
          </button>
        </div>
      }
    >
      <div data-testid="admin-partners-page">
        <div className="section-eyebrow">Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Influencer / partner referrals</h1>
        <p className="text-neutral-500 text-[13px] mt-1.5 max-w-2xl leading-relaxed">
          Each partner gets a vanity URL like <span className="font-mono">/r/&lt;slug&gt;</span>. Visitors who sign up through that link are attributed
          to the partner and bypass the waitlist. The linked email is the partner admin —
          they see their referred users + funnel stats at <span className="font-mono">/partner</span>.
        </p>

        {err && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>
            {err}
          </div>
        )}

        <div className="card overflow-visible mt-6" data-testid="admin-partners-table">
          <table className="w-full text-[13px]">
            <thead className="text-left text-neutral-500 bg-neutral-50">
              <tr>
                <th className="py-2 px-3 font-medium">Partner</th>
                <th className="py-2 px-3 font-medium">Slug · URL</th>
                <th className="py-2 px-3 font-medium">Linked admin</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium text-right">Clicks</th>
                <th className="py-2 px-3 font-medium text-right">Signups</th>
                <th className="py-2 px-3 font-medium">Created</th>
                <th className="py-2 px-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-neutral-500 text-[13px]">
                    No partners yet. Click "Add partner" to create one.
                  </td>
                </tr>
              )}
              {partners.map((p) => {
                const stats = p.stats || {};
                return (
                  <tr key={p.id} className="border-t border-black/5 hover:bg-neutral-50/40" data-testid={`admin-partner-row-${p.slug}`}>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {p.logo_url && (
                          <img
                            src={`${BACKEND_URL}${p.logo_url}`}
                            alt=""
                            className="h-8 w-8 rounded-md border border-black/5 bg-white object-contain shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{p.name}</div>
                          <div className="text-[11px] text-neutral-500 font-mono truncate">{p.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[12px]">/r/{p.slug}</span>
                        <button
                          type="button"
                          onClick={() => copyLink(p.slug)}
                          className="text-neutral-400 hover:text-black p-1 rounded"
                          title="Copy full URL"
                          data-testid={`admin-partner-copy-${p.slug}`}
                        >
                          <Copy size={11} />
                        </button>
                        {copiedSlug === p.slug && (
                          <span className="text-[10.5px] text-emerald-600 font-semibold">Copied</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-[12px] text-neutral-600 truncate" title={p.user_id || ""}>
                      {p.user_id ? <span className="font-mono">{p.user_id}</span> : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="py-2 px-3"><StatusPill status={p.status} /></td>
                    <td className="py-2 px-3 tabular text-right">{(stats.clicks || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 tabular text-right">{(stats.signups || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 text-neutral-600 text-[12px] whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(p)}
                          className="inline-flex items-center gap-1 text-[11.5px] text-neutral-500 hover:text-black p-1.5 rounded"
                          title="Edit partner"
                          data-testid={`admin-partner-edit-${p.slug}`}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        {p.status === "active" ? (
                          <button
                            type="button"
                            onClick={() => archive(p)}
                            className="inline-flex items-center gap-1 text-[11.5px] text-neutral-500 hover:text-red-700 p-1.5 rounded"
                            title="Archive partner"
                            data-testid={`admin-partner-archive-${p.slug}`}
                          >
                            <Archive size={12} /> Archive
                          </button>
                        ) : (
                          <span className="text-[11px] text-neutral-400">archived</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <AddPartnerModal open={open} onClose={() => setOpen(false)} onCreated={refresh} />
      <EditPartnerModal partner={editing} onClose={() => setEditing(null)} onUpdated={refresh} />
    </AdminLayout>
  );
}
